import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { ArrowPathIcon, CheckIcon, LockClosedIcon } from "@heroicons/react/24/outline";

import useCustomerDetails from "../../data/useCustomerDetails";
import usePartsLookupMutations from "../../data/usePartsLookupMutations";
import { IPricingMember } from "../../models/pricing/IPricing";
import { PartProBlackList } from "../../models/PartLookup";
import classNames from "../../utils/classNames";
import useNotification from "../notifications/useNotifications";
import ConfirmModal from "../shared/Confirm/ConfirmModal";
import Tooltip from "../shared/Tooltip";

/**
 * PartPro lookup sized for a pricing-table cell. Uses the member's UCID and
 * vehicle make so we do not have to load a full IPart.
 */
export default function PricingPartProButton({ member }: { member: IPricingMember }) {
  const { lookupMutation } = usePartsLookupMutations(member.partId);
  const { brand } = useCustomerDetails();
  const { addNotification } = useNotification();
  const queryClient = useQueryClient();

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  const isBlacklisted = PartProBlackList.includes((member.vehicleMake ?? "").toLowerCase());
  const ucid = (member.categoryUcid ?? "").trim();

  if (!ucid || isBlacklisted) return null;

  const unlocked = member.partsLookupRequestId != null || member.lastPartLookupChecked != null;

  const handleLookup = () => {
    if (unlocked) {
      setConfirmOpen(false);
      return;
    }

    setLoading(true);
    lookupMutation.mutate(undefined, {
      onSuccess: (data) => {
        setLoading(false);
        queryClient.invalidateQueries(["pricing"]);

        if (!data.data.requestedParts || data.data.requestedParts.length === 0) {
          addNotification({ primaryText: "No Parts Found", variant: "info" });
          return;
        }

        addNotification({ primaryText: "PartPro Lookup Successful", variant: "success" });
      },
      onError: () => {
        setLoading(false);
        addNotification({ primaryText: "PartPro Lookup Failed", variant: "error" });
      },
    });

    setConfirmOpen(false);
  };

  return (
    <>
      <ConfirmModal
        open={confirmOpen}
        setOpen={setConfirmOpen}
        onConfirm={handleLookup}
        isLoading={loading}
        title="Activate PartPro Lookup?"
        message={`This will start a PartPro lookup on this vehicle. It costs 1 token per vehicle. You have ${brand.data?.data.lookupTokens ?? 0} tokens remaining.`}
        confirmButtonText={null}
      />

      <Tooltip
        content={unlocked ? "PartPro lookup already run for this vehicle" : "Run a PartPro lookup (costs 1 token per vehicle)"}
        className="flex-none"
      >
        <button
          type="button"
          onClick={(event) => {
            event.stopPropagation();
            if (!unlocked && !loading) setConfirmOpen(true);
          }}
          disabled={unlocked || loading}
          aria-label={unlocked ? "PartPro lookup already run" : "Run a PartPro lookup"}
          className={classNames(
            "flex-none rounded p-0.5 transition-colors",
            unlocked ? "cursor-default text-emerald-600" : "text-primary-600 hover:bg-primary-50",
            loading && "animate-pulse"
          )}
        >
          {loading ? (
            <ArrowPathIcon className="h-4 w-4 animate-spin" aria-hidden="true" />
          ) : unlocked ? (
            <CheckIcon className="h-4 w-4" aria-hidden="true" />
          ) : (
            <LockClosedIcon className="h-4 w-4" aria-hidden="true" />
          )}
        </button>
      </Tooltip>
    </>
  );
}
